import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';

import { 
  Plus, 
  Edit, 
  Trash2, 
  Settings, 
  Mail, 
  Code,
  Palette,
  User,
  Building,
  DollarSign,
  Calendar,
  FileText
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';

interface CustomVariable {
  id: string;
  name: string;
  key: string;
  description: string;
  type: 'text' | 'number' | 'date' | 'currency' | 'boolean' | 'select';
  defaultValue?: string;
  options?: string[];
  isRequired: boolean;
  companyId: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: string;
  variables: string[];
}

// interface Company {
//   id: string;
//   name: string;
//   document: string;
// }

interface VariableCategory {
  name: string;
  description: string;
  icon: React.ReactNode;
  variables: CustomVariable[];
}

export const EmailPersonalizationPage: React.FC = () => {
  const navigate = useNavigate();
  const [customVariables, setCustomVariables] = useState<CustomVariable[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'variables' | 'templates' | 'preview'>('variables');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [overwriteImport, setOverwriteImport] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<CustomVariable | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    type: 'text' as 'text' | 'number' | 'date' | 'currency' | 'boolean' | 'select',
    defaultValue: '',
    options: [] as string[],
    isRequired: false,
    category: 'general',
  });

  const variableTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Data' },
    { value: 'currency', label: 'Moeda' },
    { value: 'boolean', label: 'Sim/Não' },
    { value: 'select', label: 'Seleção' },
  ];

  const categories = [
    { value: 'general', label: 'Geral', icon: <Settings className="w-4 h-4" /> },
    { value: 'company', label: 'Empresa', icon: <Building className="w-4 h-4" /> },
    { value: 'customer', label: 'Cliente', icon: <User className="w-4 h-4" /> },
    { value: 'financial', label: 'Financeiro', icon: <DollarSign className="w-4 h-4" /> },
    { value: 'temporal', label: 'Temporal', icon: <Calendar className="w-4 h-4" /> },
    { value: 'document', label: 'Documento', icon: <FileText className="w-4 h-4" /> },
  ];

  // Preview / Validação de template
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateVars, setTemplateVars] = useState<string[]>([]);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({});

  const [validation, setValidation] = useState<{ isValid: boolean; errors: string[]; warnings: string[]; missingVariables: string[]; unusedVariables: string[] } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadCustomVariables = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-personalization/variables');
      setCustomVariables(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar variáveis personalizadas');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      setTemplates(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar templates');
    }
  };

  const loadVariableTypes = async () => {
    try {
      await api.get('/email-personalization/variables/types');
      // Os variable types já estão definidos estaticamente, mas poderiam vir da API
    } catch (error: any) {
      console.warn('Erro ao carregar variable types:', error);
    }
  };

  const loadVariableCategories = async () => {
    try {
      await api.get('/email-personalization/variables/categories');
      // As categories já estão definidas estaticamente, mas poderiam vir da API
    } catch (error: any) {
      console.warn('Erro ao carregar variable categories:', error);
    }
  };

  const loadTemplateVariables = async (templateId: string) => {
    if (!templateId) {
      setTemplateVars([]);
      setPreviewVars({});
      setValidation(null);
      return;
    }
    try {
      const res = await api.get(`/email-personalization/templates/${templateId}/variables`);
      const tv: string[] = Array.isArray(res.data?.data?.templateVariables) ? res.data.data.templateVariables : [];
      setTemplateVars(tv);
      const nextVars: Record<string, string> = { ...previewVars };
      tv.forEach(k => { if (nextVars[k] === undefined) nextVars[k] = ''; });
      setPreviewVars(nextVars);
      setValidation(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar variáveis do template');
    }
  };

  const fillSampleData = async () => {
    if (!selectedTemplateId) {
      toast.error('Selecione um template');
      return;
    }
    try {
      setLoadingPreview(true);
      const res = await api.get(`/email-personalization/templates/${selectedTemplateId}/sample-data`);
      const sample = res.data?.data || {};
      setPreviewVars(sample);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao gerar dados de exemplo');
    } finally {
      setLoadingPreview(false);
    }
  };

  const validateTemplateVariables = async () => {
    if (!selectedTemplateId) {
      toast.error('Selecione um template');
      return;
    }
    try {
      setLoadingPreview(true);
      const res = await api.post('/email-personalization/validate', {
        templateId: selectedTemplateId,
        variables: previewVars,
      });
      setValidation(res.data?.data);
      if (res.data?.data?.isValid) toast.success('Variáveis válidas');
      else toast.error('Algumas variáveis precisam de atenção');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao validar variáveis');
    } finally {
      setLoadingPreview(false);
    }
  };

  const generatePreview = async () => {
    if (!selectedTemplateId) {
      toast.error('Selecione um template');
      return;
    }
    try {
      setLoadingPreview(true);
      await api.post('/email-personalization/preview', {
        templateId: selectedTemplateId,
        variables: previewVars,
      });

      toast.success('Preview gerado');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao gerar preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const exportVariables = async (format: 'csv' | 'json' = 'csv') => {
    try {
      const res = await api.get(`/email-personalization/variables/export?format=${format}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `custom-variables.${format === 'csv' ? 'csv' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportação iniciada');
    } catch (error) {
      toast.error('Erro ao exportar variáveis');
    }
  };

  const importVariables = async () => {
    try {
      const variables = JSON.parse(jsonText);
      if (!Array.isArray(variables)) {
        toast.error('JSON deve ser um array de variáveis');
        return;
      }
      const res = await api.post('/email-personalization/variables/import', {
        variables,
        overwrite: overwriteImport,
      });
      toast.success(res.data?.message || 'Importação concluída');
      setShowImportModal(false);
      setJsonText('');
      loadCustomVariables();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao importar variáveis');
    }
  };

  const bulkCreateVariables = async () => {
    try {
      const variables = JSON.parse(jsonText);
      if (!Array.isArray(variables)) {
        toast.error('JSON deve ser um array de variáveis');
        return;
      }
      const res = await api.post('/email-personalization/variables/bulk', { variables });
      toast.success(res.data?.message || 'Criação em lote concluída');
      setShowBulkModal(false);
      setJsonText('');
      loadCustomVariables();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar variáveis em lote');
    }
  };



  useEffect(() => {
    loadCustomVariables();
    loadTemplates();
    loadVariableTypes();
    loadVariableCategories();
  }, []);

  const handleCreateVariable = async () => {
    try {
      await api.post('/email-personalization/variables', formData);
      toast.success('Variável personalizada criada com sucesso');
      setShowCreateModal(false);
      resetForm();
      loadCustomVariables();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar variável personalizada');
    }
  };

  const handleUpdateVariable = async () => {
    if (!selectedVariable) return;
    
    try {
      await api.put(`/email-personalization/variables/${selectedVariable.id}`, formData);
      toast.success('Variável personalizada atualizada com sucesso');
      setShowEditModal(false);
      resetForm();
      loadCustomVariables();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar variável personalizada');
    }
  };

  const handleDeleteVariable = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta variável personalizada?')) return;
    
    try {
      await api.delete(`/email-personalization/variables/${id}`);
      toast.success('Variável personalizada excluída com sucesso');
      loadCustomVariables();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir variável personalizada');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      key: '',
      description: '',
      type: 'text',
      defaultValue: '',
      options: [],
      isRequired: false,
      category: 'general',
    });
  };

  const openEditModal = (variable: CustomVariable) => {
    setSelectedVariable(variable);
    setFormData({
      name: variable.name,
      key: variable.key,
      description: variable.description,
      type: variable.type as 'text' | 'number' | 'date' | 'currency' | 'boolean' | 'select',
      defaultValue: variable.defaultValue || '',
      options: variable.options || [],
      isRequired: variable.isRequired,
      category: variable.category,
    });
    setShowEditModal(true);
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, ''],
    });
  };

  const removeOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const getTypeLabel = (type: string) => {
    return variableTypes.find(t => t.value === type)?.label || type;
  };



  const groupVariablesByCategory = (): VariableCategory[] => {
    const grouped = categories.map(category => ({
      name: category.label,
      description: category.value,
      icon: category.icon,
      variables: customVariables.filter(v => v.category === category.value),
    }));
    
    return grouped.filter(group => group.variables.length > 0);
  };

  const generateVariableKey = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      key: generateVariableKey(name),
    });
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Personalização de Templates</h1>
          <p className="text-muted-foreground">Crie variáveis personalizadas para seus templates de email</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/configuracoes')}>← Voltar para Configurações &gt; E-mails</Button>
          <Button variant="outline" onClick={() => exportVariables('csv')}>Exportar CSV</Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>Importar (JSON)</Button>
          <Button variant="outline" onClick={() => setShowBulkModal(true)}>Criar em Lote (JSON)</Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Variável
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="variables" className="flex items-center">
            <Code className="w-4 h-4 mr-2" />
            Variáveis
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center">
            <Palette className="w-4 h-4 mr-2" />
            Preview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="variables" className="mt-6">
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <div className="space-y-6">
              {groupVariablesByCategory().map((category) => (
                <Card key={category.description}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      {category.icon}
                      <span className="ml-2">{category.name}</span>
                      <Badge variant="secondary" className="ml-2">
                        {category.variables.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {category.variables.map((variable) => (
                        <div key={variable.id} className="flex justify-between items-center p-4 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium">{variable.name}</h4>
                              <Badge variant="outline">{getTypeLabel(variable.type)}</Badge>
                              {variable.isRequired && (
                                <Badge variant="destructive">Obrigatório</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{variable.description}</p>
                                                         <p className="text-xs text-muted-foreground mt-1">
                               Chave: <code className="bg-muted px-1 rounded">{`{{${variable.key}}}`}</code>
                             </p>
                            {variable.defaultValue && (
                              <p className="text-xs text-muted-foreground">
                                Padrão: {variable.defaultValue}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(variable)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteVariable(variable.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Templates com Variáveis Personalizadas</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Variáveis Padrão</TableHead>
                    <TableHead>Variáveis Personalizadas</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => {
                    const customVars = customVariables.filter(v => 
                      template.variables.includes(v.key)
                    );
                    
                    return (
                      <TableRow key={template.id}>
                        <TableCell className="font-medium">{template.name}</TableCell>
                        <TableCell>{template.category}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {template.variables.map((variable) => (
                              <Badge key={variable} variant="outline" className="text-xs">
                                {variable}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {customVars.map((variable) => (
                              <Badge key={variable.id} variant="default" className="text-xs">
                                {variable.name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Preview de Template Personalizado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Selecionar Template</label>
                  <Select
                    value={selectedTemplateId}
                    onValueChange={(value) => { setSelectedTemplateId(value); loadTemplateVariables(value); }}
                  >
                    <option value="">Escolha um template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Select>
                </div>
                {selectedTemplateId && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Variáveis do Template</h4>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={fillSampleData} disabled={loadingPreview}>Preencher com Exemplo</Button>
                        <Button variant="outline" onClick={validateTemplateVariables} disabled={loadingPreview}>Validar</Button>
                        <Button onClick={generatePreview} disabled={loadingPreview}>Gerar Preview</Button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {templateVars.map((key) => (
                        <div key={key}>
                          <label className="block text-xs font-medium mb-1 text-muted-foreground">{key}</label>
                          <Input
                            value={previewVars[key] || ''}
                            onChange={(e) => setPreviewVars({ ...previewVars, [key]: e.target.value })}
                            placeholder={`Valor para {{${key}}}`}
                          />
                        </div>
                      ))}
                    </div>
                    {validation && (
                      <div className="border border-border rounded p-3 bg-card">
                        <h5 className="font-medium mb-2">Validação</h5>
                        {!validation.isValid && (
                          <ul className="list-disc ml-5 text-red-600 text-sm">
                            {validation.errors.map((err, i) => (<li key={i}>{err}</li>))}
                          </ul>
                        )}
                        {validation.warnings?.length > 0 && (
                          <ul className="list-disc ml-5 text-amber-600 text-sm mt-2">
                            {validation.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Variáveis (JSON)"
      >
        <div className="space-y-3">
          <textarea
            className="w-full h-56 border border-border rounded p-2 bg-card text-sm"
            placeholder='[{"name":"...","key":"...","type":"text","description":"...","defaultValue":"","options":[],"isRequired":false,"category":"general"}]'
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
          />
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={overwriteImport} onChange={(e) => setOverwriteImport(e.target.checked)} />
            Sobrescrever variáveis existentes (por chave)
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancelar</Button>
            <Button onClick={importVariables}>Importar</Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Create Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title="Criar Variáveis em Lote (JSON)"
      >
        <div className="space-y-3">
          <textarea
            className="w-full h-56 border border-border rounded p-2 bg-card text-sm"
            placeholder='[{"name":"...","key":"...","type":"text","description":"...","defaultValue":"","options":[],"isRequired":false,"category":"general"}]'
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowBulkModal(false)}>Cancelar</Button>
            <Button onClick={bulkCreateVariables}>Criar</Button>
          </div>
        </div>
      </Modal>

      {/* Create Variable Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nova Variável Personalizada"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome da Variável</label>
            <Input
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ex: Nome do Gerente"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Chave da Variável</label>
            <Input
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              placeholder="Ex: nome_gerente"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Esta chave será usada no template como {'{chave}'}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o propósito desta variável"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
              <Select
                value={formData.type}
                onValueChange={(value: any) => setFormData({ ...formData, type: value })}
              >
                {variableTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Categoria</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {formData.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Opções</label>
              {formData.options.map((option, index) => (
                <div key={index} className="flex space-x-2 mb-2">
                  <Input
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Opção ${index + 1}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeOption(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addOption}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar Opção
              </Button>
            </div>
          )}

          {formData.type !== 'select' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Valor Padrão</label>
              <Input
                value={formData.defaultValue}
                onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
                placeholder="Valor padrão (opcional)"
              />
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isRequired"
              checked={formData.isRequired}
              onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="isRequired" className="text-sm font-medium">
              Variável obrigatória
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreateVariable}>
            Criar Variável
          </Button>
        </div>
      </Modal>

      {/* Edit Variable Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Variável Personalizada"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome da Variável</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Nome do Gerente"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Chave da Variável</label>
            <Input
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              placeholder="Ex: nome_gerente"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Esta chave será usada no template como {'{chave}'}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o propósito desta variável"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
              <Select
                value={formData.type}
                onValueChange={(value: any) => setFormData({ ...formData, type: value })}
              >
                {variableTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Categoria</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {formData.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Opções</label>
              {formData.options.map((option, index) => (
                <div key={index} className="flex space-x-2 mb-2">
                  <Input
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Opção ${index + 1}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeOption(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addOption}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar Opção
              </Button>
            </div>
          )}

          {formData.type !== 'select' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Valor Padrão</label>
              <Input
                value={formData.defaultValue}
                onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
                placeholder="Valor padrão (opcional)"
              />
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isRequiredEdit"
              checked={formData.isRequired}
              onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="isRequiredEdit" className="text-sm font-medium">
              Variável obrigatória
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleUpdateVariable}>
            Atualizar Variável
          </Button>
        </div>
      </Modal>
    </div>
  );
};
